import Cookies from "js-cookie";
import Utils from "./../../Scalesoft.Shared.FrontEnd/Scripts/Utils";
import { interceptFetch } from "./InterceptFetch";

/**
 * A mechanism for user sign out is primarily controlled here in JavaScript.
 * Login expiration is also checked in server middleware to ensure correct user sign out.
 * It is possible to use HTTP header for preventing period calls from prolonging login.
 */
export default class LoginExpiration {
    private readonly expirationSeconds: number;
    private currentSeconds: number;
    private readonly criticalSeconds: number;
    private readonly logoutUrl?: string;
    private readonly redirectUrl?: string;
    private readonly refreshUrl?: string;
    private readonly loginExpirationTimeSpan: JQuery;
    private readonly loginExpirationWarning: JQuery;
    private readonly refreshButton: JQuery;
    private intervalId: number | null = null;
    private readonly utils: Utils;
    private readonly lastRequestTimeCookieName: string;
    private readonly jq: JQueryStatic;

    constructor(jQuery: JQueryStatic) {
        this.jq = jQuery;
        const script = this.jq("#login-expiration-script");
        this.expirationSeconds = parseInt(script.attr("data-login-expiration-time") ?? "", 10);
        this.currentSeconds = this.expirationSeconds;
        this.criticalSeconds = parseInt(script.attr("data-login-expiration-show-warning-time") ?? "", 10);
        this.redirectUrl = script.attr("data-redirect-after-login-expiration-url");
        this.refreshUrl = script.attr("data-login-refresh-url");
        this.logoutUrl = script.attr("data-logout-url");
        if (!this.redirectUrl || !this.refreshUrl || !this.logoutUrl) {
            throw Error("Some URL attribute for LoginExpiration not found");
        }
        this.loginExpirationTimeSpan = this.jq("#login-expiration-countdown");
        this.loginExpirationWarning = this.jq("#login-expiration-warning");
        this.refreshButton = this.jq("#login-refresh-button");
        this.utils = new Utils();
        const lastRequestTimeCookieName = script.attr("data-cookie-name");
        if (!lastRequestTimeCookieName) {
            throw Error("lastRequestTimeCookieName attribute not found");
        } else {
            this.lastRequestTimeCookieName = lastRequestTimeCookieName;
        }

        this.updateLastRequestCookie(this.utils.getPageLoadTime());
    }

    public static canInit(): boolean {
        return document.getElementById("login-expiration-script") !== null;
    }

    public init(): void {
        this.configureJqueryAjaxForRefresh();

        this.showTime();

        this.intervalId = window.setInterval(() => {
                this.tick();
            },
            1000);

        this.refreshButton.on("click", (event) => {
            event.preventDefault();

            const refreshRequest = this.jq.ajax({
                url: this.refreshUrl,
            }) as JQuery.Promise<any>;
            // Redirect to expired page when Bad request is returned otherwise general method for ajaxComplete is used for refreshing timeout
            refreshRequest.fail(() => {
                this.redirect();
            });
        });

     
        const $closeBtn = this.loginExpirationWarning.find('[data-bs-dismiss="alert"]');
        $closeBtn.removeAttr("data-bs-dismiss");
        $closeBtn.on("click", (e) => {
            this.closeWarning();
        });

    }

    private updateLastRequestCookie(nowTimestamp: number): void {
        const correctedTime = nowTimestamp + this.utils.getServerTimeCorrection();
        Cookies.set(this.lastRequestTimeCookieName, correctedTime.toString(), {secure: true, sameSite: "strict"});
    }

    private refreshInterval(): void {
        this.updateLastRequestCookie(Date.now());

        this.loginExpirationWarning.addClass("d-none");

        this.intervalId = window.setInterval(() => {
                this.tick();
            },
            1000);
    }

    private logout(redirectUrl: string): void {
        window.location.href = encodeURI(this.logoutUrl + "?redirect=" + redirectUrl);
    }

    private redirect(): void {
        window.location.href = encodeURI(this.redirectUrl + "?redirect=" + window.location.href);
    }

    private getNoActivityRedirectUrl(): string {
        return this.redirectUrl + "?redirect=" + window.location.href;
    }

    private redirectToSignoutUrlIfProvided(request: JQuery.jqXHR): void {
        const redirectUrl = request.getResponseHeader("X-Signout-Redirect-Url");
        if (redirectUrl) {
            window.location.replace(redirectUrl);
        }
    }

    private redirectToSignoutUrlIfProvided2(response: Response): void {
        const redirectUrl = response.headers.get("X-Signout-Redirect-Url");
        if (redirectUrl) {
            window.location.replace(redirectUrl);
        }
    }

    private tick(): void {
        const serverStartingTime = parseInt(Cookies.get(this.lastRequestTimeCookieName) ?? "", 10);
        const startingTime = serverStartingTime - this.utils.getServerTimeCorrection();
        const diff = Math.floor((Date.now() - startingTime) / 1000);

        this.currentSeconds = this.expirationSeconds - diff;

        if (this.currentSeconds <= 0) {
            this.loginExpired();
        }

        if (this.criticalSeconds >= this.currentSeconds) {

            if (this.loginExpirationWarning.hasClass("d-none")) {
                this.loginExpirationWarning.removeClass("d-none");
            }

            this.showTime();
        } else {
            if (!this.loginExpirationWarning.hasClass("d-none")) {
                this.loginExpirationWarning.addClass("d-none");
            }
        }
    }

    /* Called when login expired.
    Stops interval, unbinds refresh button and wait 1 second to redirect.
    If redirecting just after countdown is finished, auth cookie can be still valid */
    private loginExpired(): void {
        if (this.intervalId !== null) {
            clearInterval(this.intervalId);
        }
        this.refreshButton.off("click");
        this.logout(this.getNoActivityRedirectUrl());
    }

    private showTime(): void {
        const date = new Date(this.currentSeconds * 1000);
        let timeString: string;

        if (date.getUTCHours() > 0) {
            timeString = `${this.padZero(date.getUTCHours())}:${this.padZero(date.getUTCMinutes())}:${this.padZero(date.getUTCSeconds())}`;
        } else {
            timeString = `${this.padZero(date.getUTCMinutes())}:${this.padZero(date.getUTCSeconds())}`;
        }

        this.loginExpirationTimeSpan.text(timeString);
    }

    private padZero(num: number): string {
        return (0 <= num && num < 10 ? "0" : "") + num;
    }

    // Refresh interval for each completed ajax request to server
    private configureJqueryAjaxForRefresh(): void {
        const portalJqDocumentEl = this.jq(document as any);

        portalJqDocumentEl.on("ajaxSend", (event, response, settings) => {
            this.onAjaxSend(settings);
        });

        portalJqDocumentEl.on("ajaxComplete", (event, response, settings) => {
            this.onAjaxComplete(response, settings);
        });

        if (typeof this.jq !== "undefined" && this.jq.hasOwnProperty("ajax")) {
            const modulesJqDocumentEl = this.jq(document as any);
            modulesJqDocumentEl.on("ajaxSend", (event, response, settings) => {
                this.onAjaxSend(settings);
            });

            modulesJqDocumentEl.on("ajaxComplete", (event, response, settings) => {
                this.onAjaxComplete(response, settings);
            });
        }
    }

    public initFetch(): void {
        interceptFetch(this.refreshInterval.bind(this), this.intervalId);
    }

    private onAjaxSend(settings: JQuery.AjaxSettings): void {
        if (typeof settings.headers !== "undefined" &&
            settings.headers[this.utils.getDisableLoginProlongHttpHeaderName()]?.toString() === "true") {
            return;
        }

        if (this.utils.isRequestLocal(settings) && this.intervalId !== null) { // Clear interval only when local requests are sent
            clearInterval(this.intervalId);
        }
    }

    private onAjaxComplete(request: JQuery.jqXHR, settings: JQuery.AjaxSettings): void {
        if (this.utils.isRequestLocal(settings)) { // Sign out forced by server only when local responses are received
            this.redirectToSignoutUrlIfProvided(request);
        }

        if (typeof settings.headers !== "undefined" &&
            settings.headers[this.utils.getDisableLoginProlongHttpHeaderName()]?.toString() === "true") {
            return;
        }

        if (this.utils.isRequestLocal(settings)) { // Refresh only when local responses are received
            this.refreshInterval();
        }
    }

    private closeWarning(): void {
        this.loginExpirationWarning.hide();
    }
}
